import React from "react";
import { useMemo } from "react";
import NumberFormat from "react-number-format";
import useCart from "../../../hooks/useCart";
import { IBenefitResult } from "../../../interfaces/IBenefitResult";
import { getBenefitItem } from "../../../shared/benefitHelpers";
import { getProductTypeAsString } from "../../../shared/enumHelpers";
import { QuoteSettingContainerType } from "../../../interfaces/settings/QuoteSettingContainerType";
import cx from "classnames";

//styles
import styles from "./QuoteBreakdownClientBenefits.module.scss";

interface IQuoteBreakdownClientBenefitsProps {
    setting: QuoteSettingContainerType;
    benefits: IBenefitResult[];
}

const BenefitRow: React.FC<{ title: React.ReactNode; value: number }> = ({
    title,
    value
}) => {
    return (
        <div
            className={cx(styles["apply-result-section--line-item"], "text-font-size-s")}>
            <div className={cx("text-primary")}>{title}</div>
            <NumberFormat
                value={value}
                prefix={"$"}
                displayType={"text"}
                thousandSeparator
                isNumericString
                decimalScale={2}
                fixedDecimalScale
                className={cx("text-primary", {
                    ["text-brand"]: value < 0
                })}
            />
        </div>
    );
};

const QuoteBreakdownClientBenefits: React.FC<IQuoteBreakdownClientBenefitsProps> = (
    props
) => {
    const { setting, benefits } = props;

    const { getGroupedDiscounts } = useCart();

    // List of all discounts
    const discounts = useMemo(
        () => benefits.map((s) => s.discounts).reduce((a, b) => a.concat(b), []),
        [benefits]
    );

    const discountList = useMemo(() => getGroupedDiscounts(discounts), [discounts]);

    return (
        <div className={cx("padding-vertical-m", styles["container"])}>
            <p className={cx("gap-s text-primary text-bold text-font-size-s")}>
                {getProductTypeAsString(setting.productType).toUpperCase()}
            </p>
            {benefits.map((benefit, index) => {
                return (
                    <BenefitRow
                        key={index}
                        title={getBenefitItem(benefit, [setting])}
                        value={benefit.premium}
                    />
                );
            })}
            {discountList
                .filter((d) => d.value > 0) // We don't want to display discounts, that don't actually discount anything
                .map((discount) => {
                    return (
                        <BenefitRow
                            key={discount.title}
                            title={discount.title}
                            value={-discount.value}
                        />
                    );
                })}
            {benefits.length <= 0 && (
                <div
                    className={cx(
                        styles["apply-result-section--line-item"],
                        "text-italic"
                    )}>
                    Not covered
                </div>
            )}
        </div>
    );
};

export default QuoteBreakdownClientBenefits;
